import range from 'lodash/range';
import type { OnlinePlannerSubject } from '../../../Interfaces/OnlinePlannerSubject';
import { mapOnlinePlannerSubjectToSubjectBase } from '../utils';
import type { SubjectCategory } from '~/Features/StudyPage/Common/Interfaces/SubjectCategory';
import type {
    PlannerContentItem,
    PlannerContentItemElectiveSubjects,
    PlannerContentItemObligatorySubjects,
    PlannerFilterType,
    PlannerSubjectsGroupingType,
} from './types';

export const groupSubjects = (
    subjects: OnlinePlannerSubject[],
    filterOptions: Map<PlannerFilterType, string>,
    groupingType: PlannerSubjectsGroupingType
): Map<string, PlannerContentItem> =>
    groupingType === 'subjects'
        ? groupSubjectsByCategory(subjects, filterOptions)
        : groupSubjectsByTime(subjects, groupingType);

const groupSubjectsByTime = (subjects: OnlinePlannerSubject[], time: 'partTime' | 'fullTime') => {
    const semesterMap = new Map<number, PlannerContentItem>();

    for (const subject of subjects) {
        const semesterDefinition = subject.semesterDefinition[time];

        if (semesterDefinition) {
            const { semesterStart, semesterEnd } = semesterDefinition;

            if (semesterStart === semesterEnd) {
                createOrUpdateEntry(semesterMap, subject, semesterStart);
            }

            if (semesterStart !== semesterEnd) {
                range(semesterStart, semesterEnd + 1).forEach(value => {
                    createOrUpdateEntry(semesterMap, subject, value);
                });
            }
        }
    }

    return new Map<string, PlannerContentItem>(
        Array.from(semesterMap)
            .sort(([semesterA], [semesterB]) => semesterA - semesterB)
            .map(([key, value]) => [key.toString(), value])
    );
};

const groupSubjectsByCategory = (subjects: OnlinePlannerSubject[], filterOptions: Map<PlannerFilterType, string>) => {
    const categoryMap = new Map<SubjectCategory, PlannerContentItem>();

    for (const subject of subjects) {
        const key = subject.subjectCategory;

        if (!categoryMap.has(key)) {
            categoryMap.set(key, {
                container: {
                    type: 'subjects',
                    key,
                },
                elements: { obligatory: { subjects: [] }, elective: new Map() },
            });
        }

        const currentValues = categoryMap.get(key);

        if (!currentValues) {
            throw Error(`Online Planner: Cannot access elements under ${key}`);
        }

        const { obligatory } = currentValues.elements;
        updateObligatorySubjects(obligatory, subject);
    }

    const filtersOrder = Array.from(filterOptions.keys());

    return new Map<string, PlannerContentItem>(
        Array.from(categoryMap).sort(
            ([categoryA], [categoryB]) => filtersOrder.indexOf(categoryA) - filtersOrder.indexOf(categoryB)
        )
    );
};

const createOrUpdateEntry = (
    groupedSubjects: Map<number, PlannerContentItem>,
    subject: OnlinePlannerSubject,
    semesterStart: number
) => {
    const { electiveGroupCode } = subject;

    if (!groupedSubjects.has(semesterStart)) {
        groupedSubjects.set(semesterStart, {
            container: {
                type: 'semesters',
                key: `${semesterStart}`,
            },
            elements: { obligatory: { subjects: [] }, elective: new Map() },
        });
    }

    const currentValues = groupedSubjects.get(semesterStart);

    if (!currentValues) {
        throw Error(`Online Planner: Cannot access elements under ${semesterStart}`);
    }

    if (electiveGroupCode) {
        const { elective } = currentValues.elements;
        updateElectiveSubjects(elective, subject);
    } else {
        const { obligatory } = currentValues.elements;
        updateObligatorySubjects(obligatory, subject);
    }
};

export const updateElectiveSubjects = (
    currentElectiveSubjects: PlannerContentItemElectiveSubjects,
    subject: OnlinePlannerSubject
) => {
    const { electiveGroupCode } = subject;

    if (!electiveGroupCode) {
        throw Error(`Online Planner: Cannot group elective subjects without elective group code: ${subject.code}`);
    }

    const mappedSubject = mapOnlinePlannerSubjectToSubjectBase(subject);

    if (currentElectiveSubjects.has(electiveGroupCode)) {
        const currentValue = currentElectiveSubjects.get(electiveGroupCode);

        if (currentValue) {
            currentValue.subjects.push(mappedSubject);
        }

        return;
    }

    currentElectiveSubjects.set(electiveGroupCode, {
        subjects: [mappedSubject],
    });
};

export const updateObligatorySubjects = (
    currentValues: PlannerContentItemObligatorySubjects,
    subject: OnlinePlannerSubject
) => {
    const mappedSubject = mapOnlinePlannerSubjectToSubjectBase(subject);
    currentValues.subjects.push(mappedSubject);
};
